/* eslint-disable max-len */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';
import { useDebouncedCallback } from 'use-debounce';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import Text from '../../../../../shared/_TBComponents/Text';
import Toggle from '../../../../../shared/_TBComponents/Toggle';
import { updateOrganizationSettings } from '../../../../login/ducks/loginActions';

const Column = styled('div')`
  display: flex;
  flex-direction: column;
  margin-right: 30px;
  max-width: 602px;
`;

const HeaderContainer = styled('div')`
  border-bottom: #E0E0E0 1px solid;
  margin-bottom: 15px;
  padding-bottom: 15px;
  padding-top: 15px;
`;

const Row = styled('div')`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 25px;
`;

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  .react-toggle .react-toggle-track {
    background-color: #E9E9EA;
  }
  .react-toggle--checked .react-toggle-track {
    background-color: ${(props) => props.theme.colors.green};
  }
  .react-toggle-thumb {
    border: none;
  }
  .react-toggle:hover:not(.react-toggle--disabled) .react-toggle-track {
    background-color: #E9E9EA;
  }
  .react-toggle--checked:hover:not(.react-toggle--disabled) .react-toggle-track {
    background-color: ${(props) => props.theme.colors.green};
  }
`;

const WeightRoomViewGlobalSettings = ({ containerWidth, flags }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const organizationSettings = useSelector((state) => state.auth.data.organizationSettings);
  const subscriptionStatus = useSelector((state) => state.sportsScience.data.subscriptionStatus);

  const accountCode = currentUser?.accountCode;
  const isAmsCoreSubscribed = subscriptionStatus?.packageSubscriptionStatus?.isSubscribed;
  const bodyHeatMapFlag = !!flags?.bodyHeatMaps;

  const [globalWeightRoomViewSettings, setGlobalWeightRoomviewSettings] = useState({
    automaticallyInputPrescribedWeights: Boolean(organizationSettings?.generalSettings?.wrvPlaceholdersEnabled),
    supersetsEnabled: Boolean(organizationSettings?.generalSettings?.wrvSupersetsEnabled),
    wrvAthleteBodyHeatMapEntry: Boolean(organizationSettings?.generalSettings?.wrvAthleteBodyHeatMapEntry),
  });

  const debouncedSetGlobalWeightRoomviewSettings = useDebouncedCallback(
    (newSettings) => {
      const valueObj = {
        generalSettings: {
          wrvAthleteBodyHeatMapEntry: newSettings.wrvAthleteBodyHeatMapEntry,
          wrvPlaceholdersEnabled: newSettings.automaticallyInputPrescribedWeights,
          wrvSupersetsEnabled: newSettings.supersetsEnabled,
        },
      };
      dispatch(updateOrganizationSettings(accountCode, valueObj));
    },
    1000,
  );

  const toggleRowsConfig = useMemo(
    () => [
      {
        description: 'In a superset, next and previous buttons will toggle by set and not exercise.',
        field: 'supersetsEnabled',
        id: 1,
        shouldRender: () => true,
        title: 'Supersets Enabled',
      },
      {
        description: 'When enabled, suggested and prescribed weights will be automatically filled in and saved when the next button or the workout is closed. If disabled, the weight values will show up as a placeholder and athletes will have to enter their weights manually before saving.',
        field: 'automaticallyInputPrescribedWeights',
        id: 2,
        shouldRender: () => true,
        title: 'Automatically Input Prescribed/Suggested Weights',
      },
      {
        description: 'When enabled, athletes will be able to enter Body Heatmap data in Weight Room View.',
        field: 'wrvAthleteBodyHeatMapEntry',
        id: 3,
        shouldRender: () => isAmsCoreSubscribed && bodyHeatMapFlag,
        title: 'Allow Athlete Entry for Body Heatmap - WRV',
      },
    ],
    [isAmsCoreSubscribed, bodyHeatMapFlag],
  );

  const handleSwitches = (field, v) => {
    setGlobalWeightRoomviewSettings((prev) => ({
      ...prev,
      [field]: v,
    }));
    debouncedSetGlobalWeightRoomviewSettings({
      ...globalWeightRoomViewSettings,
      [field]: v,
    });
  };

  const handleToggleChange = (field) => {
    handleSwitches(field, !globalWeightRoomViewSettings[field]);
  };

  const renderHeader = () => (
    <HeaderContainer>
      <Text styledTextProps={{ fontSize: containerWidth && containerWidth < 450 ? 24 : 28, fontWeight: '700' }}>
        Weight Room View - Global Settings
      </Text>
      <Text styledTextProps={{ fontSize: 18, fontWeight: '400' }}>
        Manage global Weight Room View setting defaults for every device
      </Text>
    </HeaderContainer>
  );

  const renderToggleRow = (rowConfig) => (
    <Row key={rowConfig.id}>
      <Column>
        <Text styledTextProps={{ fontSize: 18, fontWeight: '700' }} styledWrapperProps={{ pb: '10px' }}>
          {rowConfig.title}
        </Text>
        <Text styledTextProps={{ fontSize: 16, fontWeight: '400' }}>
          {rowConfig.description}
        </Text>
      </Column>
      <Toggle
        width='auto'
        defaultChecked={globalWeightRoomViewSettings[rowConfig.field]}
        onChange={() => handleToggleChange(rowConfig.field)}
      />
    </Row>
  );

  return (
    <Wrapper theme={theme}>
      {renderHeader()}
      {toggleRowsConfig
        .filter((rowConfig) => rowConfig.shouldRender())
        .map((rowConfig) => renderToggleRow(rowConfig))}
    </Wrapper>
  );
};

WeightRoomViewGlobalSettings.propTypes = {
  containerWidth: PropTypes.number,
  flags: PropTypes.instanceOf(Object),
};

WeightRoomViewGlobalSettings.defaultProps = {
  containerWidth: '',
  flags: {},
};

export default withLDConsumer()(WeightRoomViewGlobalSettings);
